import { useEffect, useRef } from 'react'
import { t } from '@/lib/i18n'

// A keyboard- and pointer-accessible vertical resize divider. It controls a
// RIGHT-anchored pane, so dragging LEFT widens it. The component is stateless
// about the actual width — the parent owns state + persistence and passes the
// current width back in for ARIA + drag math.
export function ResizeHandle({
  width, min, max, onResize, onReset,
}: {
  width: number
  min: number
  max: number
  onResize: (px: number) => void
  onReset: () => void
}) {
  const rafRef = useRef<number | null>(null)
  const startXRef = useRef(0)
  const startWRef = useRef(0)

  // Safety net: if the handle unmounts mid-drag (pane closed via keyboard/route
  // change), endDrag never fires — so restore the body styles + cancel any rAF
  // on unmount so a stuck 'col-resize'/userSelect:none can't leak globally.
  useEffect(() => {
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
    }
  }, [])

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    startXRef.current = e.clientX
    startWRef.current = width
    e.currentTarget.setPointerCapture(e.pointerId)
    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'
  }

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return
    // Right-anchored pane: moving the pointer left (negative delta) widens it.
    const next = startWRef.current - (e.clientX - startXRef.current)
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(() => onResize(next))
  }

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
    if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null }
    document.body.style.userSelect = ''
    document.body.style.cursor = ''
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const STEP = 16
    if (e.key === 'ArrowLeft') { e.preventDefault(); onResize(width + STEP) }   // widen
    else if (e.key === 'ArrowRight') { e.preventDefault(); onResize(width - STEP) } // narrow
  }

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-valuenow={width}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-label={t('resizeInspector')}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={onReset}
      onKeyDown={onKeyDown}
      style={{
        flexShrink: 0,
        width: 6,
        cursor: 'col-resize',
        backgroundColor: 'var(--border)',
        transition: 'background-color 120ms ease',
        alignSelf: 'stretch',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent)' }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--border)' }}
      onFocus={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent)' }}
      onBlur={(e) => { e.currentTarget.style.backgroundColor = 'var(--border)' }}
    />
  )
}
